// SettingsQuickBooksPane.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useBase } from 'context/BaseContext';

import {
  disconnectQbo,
  getQboConnection,
  previewOpenQboInvoices,
  readPublicQuickBooks,
  saveQboMapping,
  startQboConnect,
  syncOpenQboInvoices,
} from 'services/quickbooks/QuickBooksService';

import './SettingsQuickBooksPane.css';
import './SettingsNamePane.css';

import Button from 'components/button/Button';
import SettingsModalPaneContents from './SettingsModalPaneContents';
import SettingsModalPaneHeader from './SettingsModalPaneHeader';

function accountLabel(row) {
  const type = [row?.type, row?.subType].filter(Boolean).join(' · ');
  return type ? `${row.name} (${type})` : row.name;
}

function itemLabel(row) {
  return row?.type ? `${row.name} (${row.type})` : row.name;
}

function filterAccounts(accounts, matcher) {
  return (Array.isArray(accounts) ? accounts : []).filter(matcher);
}

/**
 * Settings → Financials: QuickBooks Online Connect + account mapping.
 */
const SettingsQuickBooksPane = React.memo(({
  base = null,
  canEditBase = false,
}) => {
  const { setActiveBase } = useBase();

  const persisted = useMemo(() => readPublicQuickBooks(base), [base]);

  const [connection, setConnection] = useState(persisted);
  const [accounts, setAccounts] = useState([]);
  const [items, setItems] = useState([]);
  const [incomeItemId, setIncomeItemId] = useState(persisted.defaultIncomeItemId || '');
  const [arAccountId, setArAccountId] = useState(persisted.defaultArAccountId || '');
  const [depositAccountId, setDepositAccountId] = useState(persisted.depositAccountId || '');
  const [isBusy, setIsBusy] = useState(false);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const applyConnection = useCallback((next, nextBasePatch = null) => {
    setConnection(next);
    setIncomeItemId(next?.defaultIncomeItemId || '');
    setArAccountId(next?.defaultArAccountId || '');
    setDepositAccountId(next?.depositAccountId || '');
    if (nextBasePatch !== false) {
      setActiveBase((previous) => {
        if (!previous || previous.key !== base?.key) return previous;
        return {
          ...previous,
          quickbooks: next,
        };
      });
    }
  }, [base?.key, setActiveBase]);

  const loadCatalog = useCallback(async () => {
    if (!base?.key || !canEditBase) return;
    setIsLoadingCatalog(true);
    try {
      const result = await getQboConnection({ baseKey: base.key, includeCatalog: true });
      applyConnection(result.connection);
      setAccounts(result.accounts);
      setItems(result.items);
    } catch (error) {
      setErrorMessage(error?.message || 'Unable to load QuickBooks accounts.');
    } finally {
      setIsLoadingCatalog(false);
    }
  }, [applyConnection, base?.key, canEditBase]);

  useEffect(() => {
    setConnection(persisted);
    setIncomeItemId(persisted.defaultIncomeItemId || '');
    setArAccountId(persisted.defaultArAccountId || '');
    setDepositAccountId(persisted.depositAccountId || '');
  }, [persisted]);

  useEffect(() => {
    if (!base?.key || !canEditBase) return undefined;
    loadCatalog();
    return undefined;
  }, [base?.key, canEditBase, loadCatalog]);

  const arAccounts = useMemo(
    () => filterAccounts(accounts, (row) => /receivable/i.test(`${row.type} ${row.subType} ${row.name}`)),
    [accounts],
  );
  const depositAccounts = useMemo(
    () => filterAccounts(
      accounts,
      (row) => /bank|other current asset|undeposited/i.test(`${row.type} ${row.subType} ${row.name}`),
    ),
    [accounts],
  );

  const mappingDirty = (
    connection.connected
    && (
      incomeItemId !== (connection.defaultIncomeItemId || '')
      || arAccountId !== (connection.defaultArAccountId || '')
      || depositAccountId !== (connection.depositAccountId || '')
    )
  );

  const controlsDisabled = !canEditBase || isBusy;

  const handleConnect = async () => {
    if (controlsDisabled || !base?.key) return;
    setErrorMessage('');
    setStatusMessage('Opening QuickBooks…');
    setIsBusy(true);
    try {
      const result = await startQboConnect({ baseKey: base.key });
      const authorizeUrl = String(result?.authorizeUrl || '').trim();
      if (!authorizeUrl) throw new Error('QuickBooks did not return a connect URL.');
      window.location.assign(authorizeUrl);
    } catch (error) {
      setErrorMessage(error?.message || 'Unable to start QuickBooks connect.');
      setStatusMessage('');
      setIsBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (controlsDisabled || !base?.key) return;
    const confirmed = window.confirm(
      'Disconnect QuickBooks? Mapping is kept. Synced invoices in QuickBooks are not deleted.',
    );
    if (!confirmed) return;
    setErrorMessage('');
    setIsBusy(true);
    try {
      const next = await disconnectQbo({ baseKey: base.key });
      applyConnection(next);
      setStatusMessage('QuickBooks disconnected.');
    } catch (error) {
      setErrorMessage(error?.message || 'Unable to disconnect QuickBooks.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleSyncOpenInvoices = async () => {
    if (controlsDisabled || !base?.key || !connection.mappingComplete) return;
    setErrorMessage('');
    setStatusMessage('Counting open invoices…');
    setIsBusy(true);
    try {
      const preview = await previewOpenQboInvoices({ baseKey: base.key });
      const count = Number(preview?.count) || 0;
      if (!count) {
        setStatusMessage('No issued or paid invoices from the last 90 days to sync.');
        return;
      }
      const confirmed = window.confirm(
        `Sync ${count} invoice${count === 1 ? '' : 's'} from the last 90 days to QuickBooks? This does not run automatically on Connect.`,
      );
      if (!confirmed) {
        setStatusMessage('');
        return;
      }
      setStatusMessage(`Syncing ${count} invoice${count === 1 ? '' : 's'}…`);
      const result = await syncOpenQboInvoices({ baseKey: base.key });
      const synced = Number(result?.synced) || 0;
      const errors = Number(result?.errors) || 0;
      if (errors) {
        setErrorMessage(`${synced} synced, ${errors} failed. Open an invoice to retry.`);
        setStatusMessage('');
      } else {
        setStatusMessage(`Synced ${synced} invoice${synced === 1 ? '' : 's'} to QuickBooks.`);
      }
    } catch (error) {
      setErrorMessage(error?.message || 'Unable to sync open invoices.');
      setStatusMessage('');
    } finally {
      setIsBusy(false);
    }
  };

  const handleSaveMapping = async () => {
    if (controlsDisabled || !base?.key) return;
    if (!incomeItemId || !arAccountId || !depositAccountId) {
      setErrorMessage('Choose an income item, A/R account, and deposit account.');
      return;
    }
    setErrorMessage('');
    setIsBusy(true);
    try {
      const next = await saveQboMapping({
        baseKey: base.key,
        defaultIncomeItemId: incomeItemId,
        defaultArAccountId: arAccountId,
        depositAccountId,
      });
      applyConnection(next);
      setStatusMessage('QuickBooks mapping saved. Invoice sync is the next step.');
    } catch (error) {
      setErrorMessage(error?.message || 'Unable to save mapping.');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <>
      <SettingsModalPaneHeader
        title="Financials"
        subtitle="One-way sync to QuickBooks Online. Appdeck stays the source of truth."
      />

      <SettingsModalPaneContents>
        <div className="settings-quickbooks-fields">
          <div className="settings-quickbooks-status">
            <div className="settings-quickbooks-status-title">
              {connection.connected
                ? (connection.companyName || 'QuickBooks connected')
                : 'QuickBooks is not connected'}
            </div>
            <div className="settings-quickbooks-status-meta">
              {connection.connected
                ? [
                  connection.environment === 'production' ? 'Production' : 'Sandbox',
                  connection.realmId ? `Company ID ${connection.realmId}` : null,
                  connection.mappingComplete ? 'Mapping complete' : 'Mapping required before invoices sync',
                ].filter(Boolean).join(' · ')
                : 'Connect a US QuickBooks Online company (sandbox is fine for now).'}
            </div>
          </div>

          {!connection.connected ? (
            <ol className="settings-quickbooks-checklist">
              <li>Use QuickBooks Online (US). Desktop is not supported.</li>
              <li>Turn off QBO Advanced automations so payments are not applied twice.</li>
              <li>After connect, map income, A/R, and a deposit account.</li>
            </ol>
          ) : null}

          <div className="settings-quickbooks-actions">
            {!connection.connected ? (
              <Button
                label={isBusy ? 'Connecting…' : 'Connect QuickBooks'}
                enabled={!controlsDisabled}
                onClick={handleConnect}
                height="32px"
                fontSize="13px"
              />
            ) : (
              <>
                <Button
                  label="Refresh accounts"
                  enabled={!controlsDisabled && !isLoadingCatalog}
                  onClick={loadCatalog}
                  height="32px"
                  fontSize="13px"
                />
                <Button
                  label="Disconnect"
                  enabled={!controlsDisabled}
                  onClick={handleDisconnect}
                  height="32px"
                  fontSize="13px"
                />
              </>
            )}
          </div>

          {connection.connected ? (
            <>
              <div className="settings-purchase-orders-section-label">
                Account mapping
              </div>

              <div className="settings-quickbooks-field">
                <div className="settings-modal-field-label">Income item</div>
                <select
                  className="settings-quickbooks-select"
                  value={incomeItemId}
                  disabled={controlsDisabled}
                  onChange={(event) => setIncomeItemId(event.target.value)}
                >
                  <option value="">Select a product or service…</option>
                  {items.map((row) => (
                    <option key={row.id} value={row.id}>{itemLabel(row)}</option>
                  ))}
                </select>
              </div>

              <div className="settings-quickbooks-field">
                <div className="settings-modal-field-label">Accounts receivable</div>
                <select
                  className="settings-quickbooks-select"
                  value={arAccountId}
                  disabled={controlsDisabled}
                  onChange={(event) => setArAccountId(event.target.value)}
                >
                  <option value="">Select A/R…</option>
                  {(arAccounts.length ? arAccounts : accounts).map((row) => (
                    <option key={row.id} value={row.id}>{accountLabel(row)}</option>
                  ))}
                </select>
              </div>

              <div className="settings-quickbooks-field">
                <div className="settings-modal-field-label">Deposit account</div>
                <select
                  className="settings-quickbooks-select"
                  value={depositAccountId}
                  disabled={controlsDisabled}
                  onChange={(event) => setDepositAccountId(event.target.value)}
                >
                  <option value="">Select undeposited funds or bank…</option>
                  {(depositAccounts.length ? depositAccounts : accounts).map((row) => (
                    <option key={row.id} value={row.id}>{accountLabel(row)}</option>
                  ))}
                </select>
              </div>

              <div className="settings-quickbooks-actions">
                <Button
                  label={isBusy ? 'Saving…' : 'Save mapping'}
                  enabled={!controlsDisabled && mappingDirty}
                  onClick={handleSaveMapping}
                  height="32px"
                  fontSize="13px"
                />
                {connection.mappingComplete ? (
                  <Button
                    label="Sync open invoices"
                    enabled={!controlsDisabled}
                    onClick={handleSyncOpenInvoices}
                    height="32px"
                    fontSize="13px"
                  />
                ) : null}
              </div>
            </>
          ) : null}

          {statusMessage ? (
            <div className="settings-quickbooks-message">{statusMessage}</div>
          ) : null}
          {errorMessage ? (
            <div className="settings-quickbooks-message is-error">{errorMessage}</div>
          ) : null}
        </div>
      </SettingsModalPaneContents>
    </>
  );
});

SettingsQuickBooksPane.displayName = 'SettingsQuickBooksPane';

export default SettingsQuickBooksPane;
